import { useState } from 'react'
import { api } from '@api'
import { useOnline } from '@hooks/useOnline.ts'
import {
	ACCESSIBILITY,
	type Accessibility,
	ACTIVITY_TYPE,
	type Activity,
	type ActivityType,
	AUDIENCE,
	type Audience,
	COST,
	type Cost,
	WEATHER_SUIT,
	type WeatherSuit,
} from '@hewey/shared'
import { Dialog } from '@components/Dialog.tsx'
import {
	ACCESSIBILITY_META,
	ACTIVITY_TYPE_META,
	AUDIENCE_META,
	COST_META,
	WEATHER_SUIT_META,
} from './activityLabels.ts'
import styles from './ActivityTagsDialog.module.css'

type Props = {
	open: boolean
	activity: Activity
	onPatched: (activity: Activity) => void
	// Pressed Done — the activity has been marked tagged and drops off the
	// "Needs tagging" list.
	onDone: () => void
	// Remind me later, ESC, or a backdrop tap. Only Done marks it tagged, so
	// every other way out leaves it pending.
	onClose: () => void
}

// The tag step, shown at the end of the add flow and again from the "Needs
// tagging" page. Pills save as they're tapped; the buttons only decide whether
// the activity stays on the list.
export const ActivityTagsDialog = ({
	open,
	activity,
	onPatched,
	onDone,
	onClose,
}: Props) => {
	const online = useOnline()
	const [finishing, setFinishing] = useState(false)

	const patch = async (
		patcher: (a: Activity) => Activity,
		body: Parameters<typeof api.activity.update>[1],
	) => {
		onPatched(patcher(activity))
		try {
			const res = await api.activity.update(activity.id, body)
			onPatched(res.activity)
		} catch {
			onPatched(activity)
		}
	}

	const toggleType = (t: ActivityType) => {
		const next = activity.types.includes(t)
			? activity.types.filter((x) => x !== t)
			: [...activity.types, t]
		void patch((a) => ({ ...a, types: next }), { types: next })
	}

	const setCost = (next: Cost | null) => {
		void patch((a) => ({ ...a, cost: next }), { cost: next })
	}

	const toggleWeather = (w: WeatherSuit) => {
		const next = activity.weatherSuits.includes(w)
			? activity.weatherSuits.filter((x) => x !== w)
			: [...activity.weatherSuits, w]
		void patch((a) => ({ ...a, weatherSuits: next }), {
			weatherSuits: next,
		})
	}

	const toggleAudience = (x: Audience) => {
		const next = activity.audience.includes(x)
			? activity.audience.filter((y) => y !== x)
			: [...activity.audience, x]
		void patch((a) => ({ ...a, audience: next }), { audience: next })
	}

	const toggleAccessibility = (x: Accessibility) => {
		const next = activity.accessibility.includes(x)
			? activity.accessibility.filter((y) => y !== x)
			: [...activity.accessibility, x]
		void patch((a) => ({ ...a, accessibility: next }), {
			accessibility: next,
		})
	}

	const markTagged = async () => {
		if (finishing) return
		setFinishing(true)
		try {
			await patch((a) => ({ ...a, taggingStatus: 'tagged' }), {
				taggingStatus: 'tagged',
			})
			onDone()
		} finally {
			setFinishing(false)
		}
	}

	return (
		<Dialog open={open} onClose={onClose}>
			<div className={styles.panel}>
				<h2 className={styles.heading}>Add tags</h2>
				<p className={styles.hint}>
					Tags decide which collections “{activity.name}” appears in.
					You can add them later if you’d rather.
				</p>

				<section className={styles.field}>
					<span className={styles.label}>Type</span>
					<div className={styles.tagPills}>
						{ACTIVITY_TYPE.map((t) => {
							const meta = ACTIVITY_TYPE_META[t]
							const active = activity.types.includes(t)
							return (
								<button
									key={t}
									type="button"
									className={`${styles.tagPill} ${active ? styles.tagPillActive : ''}`.trim()}
									aria-pressed={active}
									disabled={!online}
									onClick={() => toggleType(t)}
								>
									<span aria-hidden="true">{meta.emoji}</span>{' '}
									{meta.label}
								</button>
							)
						})}
					</div>
				</section>

				<section className={styles.field}>
					<span className={styles.label}>Cost</span>
					<div className={styles.tagPills}>
						{COST.map((c) => {
							const meta = COST_META[c]
							const active = activity.cost === c
							return (
								<button
									key={c}
									type="button"
									className={`${styles.tagPill} ${active ? styles.tagPillActive : ''}`.trim()}
									aria-pressed={active}
									disabled={!online}
									onClick={() => setCost(active ? null : c)}
								>
									<span aria-hidden="true">{meta.emoji}</span>{' '}
									{meta.label}
								</button>
							)
						})}
					</div>
				</section>

				<section className={styles.field}>
					<span className={styles.label}>Weather suits</span>
					<div className={styles.tagPills}>
						{WEATHER_SUIT.map((w) => {
							const meta = WEATHER_SUIT_META[w]
							const active = activity.weatherSuits.includes(w)
							return (
								<button
									key={w}
									type="button"
									className={`${styles.tagPill} ${active ? styles.tagPillActive : ''}`.trim()}
									aria-pressed={active}
									disabled={!online}
									onClick={() => toggleWeather(w)}
								>
									<span aria-hidden="true">{meta.emoji}</span>{' '}
									{meta.label}
								</button>
							)
						})}
					</div>
				</section>

				<section className={styles.field}>
					<span className={styles.label}>Audience</span>
					<div className={styles.tagPills}>
						{AUDIENCE.map((x) => {
							const meta = AUDIENCE_META[x]
							const active = activity.audience.includes(x)
							return (
								<button
									key={x}
									type="button"
									className={`${styles.tagPill} ${active ? styles.tagPillActive : ''}`.trim()}
									aria-pressed={active}
									disabled={!online}
									onClick={() => toggleAudience(x)}
								>
									<span aria-hidden="true">{meta.emoji}</span>{' '}
									{meta.label}
								</button>
							)
						})}
					</div>
				</section>

				<section className={styles.field}>
					<span className={styles.label}>Accessibility</span>
					<div className={styles.tagPills}>
						{ACCESSIBILITY.map((x) => {
							const meta = ACCESSIBILITY_META[x]
							const active = activity.accessibility.includes(x)
							return (
								<button
									key={x}
									type="button"
									className={`${styles.tagPill} ${active ? styles.tagPillActive : ''}`.trim()}
									aria-pressed={active}
									disabled={!online}
									onClick={() => toggleAccessibility(x)}
								>
									<span aria-hidden="true">{meta.emoji}</span>{' '}
									{meta.label}
								</button>
							)
						})}
					</div>
				</section>

				<div className={styles.actions}>
					<button
						type="button"
						className={styles.later}
						onClick={onClose}
					>
						Remind me later
					</button>
					<button
						type="button"
						className={styles.done}
						disabled={finishing || !online}
						onClick={() => void markTagged()}
					>
						{finishing ? 'Saving…' : 'Done'}
					</button>
				</div>
			</div>
		</Dialog>
	)
}
